import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';

import {
  apiPersonDetailImagesList,
  getApiPersonDetailImagesListQueryKey,
} from '@rover/rsdk/src/apiClient/latest/api-person-detail-images-list';
import {
  apiPersonDetailStayMediaList,
  getApiPersonDetailStayMediaListQueryKey,
} from '@rover/rsdk/src/apiClient/latest/api-person-detail-stay-media-list';
import type { Image } from '@rover/rsdk/src/apiClient/latest/model/image';
import type { PaginatedImageList } from '@rover/rsdk/src/apiClient/latest/model/paginatedImageList';
import emitMetric from '@rover/utilities/emitMetric';

export const GALLERY_IMAGE_PAGE_SIZE = 25;

export const GALLERY_LOAD_TIMEOUT_MS = 10_000;

const getPlatform = (): 'native' | 'web' => (process.env.JS_ENV_NATIVE ? 'native' : 'web');

export const getNextPageParam = (lastPage: { next?: string | null }): number | undefined => {
  if (!lastPage.next) return undefined;

  const url = new URL(lastPage.next);
  const nextPage = url.searchParams.get('page');

  if (!nextPage) return undefined;

  const nextPageAsNumber = Number(nextPage);
  return Number.isInteger(nextPageAsNumber) ? nextPageAsNumber : undefined;
};

export type GalleryImagesData = {
  areImagesLoading: boolean;
  galleryImages: Image[];
  imageCount: number;
  onPaginate: () => void;
  hasMore: boolean;
  isLoadingMore: boolean;
};

const useGalleryImages = ({ opk }: { opk?: string }): GalleryImagesData => {
  const {
    data: sitterImagesData,
    isLoading: isLoadingInitialSitterImages,
    hasNextPage: sitterImagesHasNextPage,
    isFetchingNextPage: isFetchingNextPageOfSitterImages,
    fetchNextPage: fetchNextPageOfSitterImages,
  } = useInfiniteQuery<PaginatedImageList>({
    queryKey: getApiPersonDetailImagesListQueryKey(opk),
    queryFn: ({ pageParam = 1 }) =>
      apiPersonDetailImagesList(opk!, { page: pageParam, pageSize: GALLERY_IMAGE_PAGE_SIZE }),
    getNextPageParam,
    enabled: !!opk,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const sitterImagesExhausted = sitterImagesData !== undefined && !sitterImagesHasNextPage;

  const {
    data: stayImagesData,
    isLoading: isLoadingInitialStayImages,
    hasNextPage: stayImagesHasNextPage,
    isFetchingNextPage: isFetchingNextPageOfStayImages,
    fetchNextPage: fetchNextPageOfStayImages,
  } = useInfiniteQuery({
    queryKey: getApiPersonDetailStayMediaListQueryKey(opk),
    queryFn: ({ pageParam = 1 }) => apiPersonDetailStayMediaList(opk!, { page: pageParam }),
    getNextPageParam,
    enabled: !!opk,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const sitterImages = useMemo(
    () => sitterImagesData?.pages.flatMap((p) => p.results ?? []) ?? [],
    [sitterImagesData]
  );

  const stayImages = useMemo(
    () => stayImagesData?.pages.flatMap((p) => (p.results ?? []).map((m) => m.object)) ?? [],
    [stayImagesData]
  );

  const galleryImages = useMemo(
    () => [...sitterImages, ...(sitterImagesExhausted ? stayImages : [])],
    [sitterImages, stayImages, sitterImagesExhausted]
  );

  const imageCount =
    (sitterImagesData?.pages[0]?.count ?? 0) + (stayImagesData?.pages[0]?.count ?? 0) + 1; // +1 accounts for the sitter's profile image prepended to the gallery

  const hasNextPage = sitterImagesHasNextPage || (stayImagesHasNextPage ?? false);
  const isFetchingNextPage = isFetchingNextPageOfSitterImages || isFetchingNextPageOfStayImages;

  const fetchNextPage = useCallback(() => {
    if (sitterImagesHasNextPage) {
      fetchNextPageOfSitterImages();
    } else if (stayImagesHasNextPage) {
      fetchNextPageOfStayImages();
    }
  }, [
    sitterImagesHasNextPage,
    fetchNextPageOfSitterImages,
    stayImagesHasNextPage,
    fetchNextPageOfStayImages,
  ]);

  const areImagesLoading = isLoadingInitialSitterImages || isLoadingInitialStayImages;

  const hasEmittedTimeoutRef = useRef(false);
  const hasEmittedEmptyRef = useRef(false);
  const imageCountRef = useRef(imageCount);
  imageCountRef.current = imageCount;

  useEffect(() => {
    hasEmittedTimeoutRef.current = false;
    hasEmittedEmptyRef.current = false;
  }, [opk]);

  useEffect(() => {
    if (!opk || !areImagesLoading) return undefined;

    const timer = setTimeout(() => {
      if (hasEmittedTimeoutRef.current) return;
      hasEmittedTimeoutRef.current = true;
      emitMetric('sitter_profile_gallery_load_timeout', {
        platform: getPlatform(),
        expected_image_count: String(imageCountRef.current),
      });
    }, GALLERY_LOAD_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [opk, areImagesLoading]);

  useEffect(() => {
    if (areImagesLoading || hasEmittedEmptyRef.current) return;
    if (imageCount > 1 && galleryImages.length === 0) {
      hasEmittedEmptyRef.current = true;
      emitMetric('sitter_profile_gallery_rendered_empty', { platform: getPlatform() });
    }
  }, [areImagesLoading, imageCount, galleryImages.length]);

  return {
    areImagesLoading,
    galleryImages,
    imageCount,
    onPaginate: fetchNextPage,
    hasMore: hasNextPage,
    isLoadingMore: isFetchingNextPage,
  };
};

export default useGalleryImages;
